import axios from 'axios';

// Use environment variable or default to HTTP for api.guyrules.wpsee.com
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  'http://api.guyrules.wpsee.com/api';

// Debug - check for spaces
//console.log('API URL:', JSON.stringify(API_BASE_URL));
//console.log('Has space?', API_BASE_URL.includes(' '));

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    // Check both localStorage (remember me) and sessionStorage for token
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error logging for debugging
    console.error('API Error:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      fullURL: error.config?.baseURL + error.config?.url,
      response: error.response?.data,
      request: error.request,
    });

    // Network errors (no response from server)
    if (!error.response) {
      console.error('Network Error Details:', {
        message: error.message,
        code: error.code,
        config: error.config,
        isNetworkError: error.code === 'ERR_NETWORK',
        isTimeout: error.code === 'ECONNABORTED',
      });
    }

    if (error.response?.status === 401) {
      // Clear tokens from both storage locations
      localStorage.removeItem('token');
      localStorage.removeItem('remember');
      sessionStorage.removeItem('token');
      
      // Get request URL
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;
      
      // Don't redirect for /auth/me - it's used to check auth status
      if (requestUrl.includes('/auth/me')) {
        // Just reject - AuthContext handles this gracefully
        return Promise.reject(error);
      }
      
      // Don't redirect if already on login/register page
      if (currentPath !== '/login' && currentPath !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Rules API
export const rulesApi = {
  getAll: (page = 1, perPage = 10) => 
    api.get(`/rules?page=${page}&per_page=${perPage}`),
  
  getById: (id) => 
    api.get(`/rules/${id}`),
  
  getRandom: () => 
    api.get('/rules/random'),
  
  getLatest: () => 
    api.get('/rules/latest'),
  
  search: (term) => 
    api.get(`/rules/search?q=${encodeURIComponent(term)}`),
  
  getCategories: () => 
    api.get('/rules/categories'),
  
  getByCategory: (categoryId, page = 1, perPage = 10) => 
    api.get(`/rules/category/${categoryId}?page=${page}&per_page=${perPage}`),
  
  create: (data) => 
    api.post('/rules', data),
  
  update: (id, data) => 
    api.put(`/rules/${id}`, data),
  
  delete: (id) => 
    api.delete(`/rules/${id}`),
  
  getDeleted: (page = 1, perPage = 10) =>
    api.get(`/rules?page=${page}&per_page=${perPage}&deleted=1`),
  
  restore: (id) =>
    api.post(`/rules/${id}/restore`),

  /** Club member Agree (1) or Disagree (0) vote. Requires auth. */
  clubApproveVote: (id, vote) =>
    api.post(`/rules/${id}/club-approve-vote`, { vote: Number(vote) }),
  
  // Category CRUD
  createCategory: (data) =>
    api.post('/categories', data),
  
  updateCategory: (id, data) =>
    api.put(`/categories/${id}`, data),
  
  deleteCategory: (id) =>
    api.delete(`/categories/${id}`),
  
  uploadCategoryIcon: (file) => {
    const formData = new FormData();
    formData.append('icon', file);
    return api.post('/categories/upload-icon', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Club Guydom API
  getClubSubmissions: (status = 'vote1') =>
    api.get(`/club/submissions${status ? `?status=${status}` : ''}`),
  
  submitClubVote: (submitId, vote) =>
    api.post('/club/vote', { submit_id: submitId, vote }),
  
  submitNewRule: (data) =>
    api.post('/club/submit', data),
};

// Submissions Workflow API (Admin)
export const submissionsApi = {
  // Legacy method name for compatibility
  getAll: (status = null) =>
    api.get(`/club/submissions/all${status ? `?status=${status}` : ''}`),
  
  // New method name matching backend
  getAllSubmissions: (status = null) =>
    api.get(`/club/submissions/all${status ? `?status=${status}` : ''}`),
  
  updateStatus: (id, status) =>
    api.put(`/club/submissions/${id}/status`, { status }),
  
  approveSubmission: (id, data) =>
    api.post(`/club/submissions/${id}/approve`, data),
  
  rejectSubmission: (id) =>
    api.post(`/club/submissions/${id}/reject`),
  
  getCategories: () =>
    api.get('/categories'),
  
  getSubmissionStatusCounts: () =>
    api.get('/club/submissions/status-counts'),

  // New workflow methods
  moveToVote1: (id, data) =>
    api.post(`/club/submissions/${id}/move-to-vote1`, data),
  
  getVote1Submissions: () =>
    api.get('/club/submissions/vote1'),
  
  moveToVote2: (id) =>
    api.post(`/club/submissions/${id}/move-to-vote2`),
  
  getVote2Submissions: () =>
    api.get('/club/submissions/vote2'),
  
  vote2: (id, vote) =>
    api.post(`/club/submissions/${id}/vote2`, { vote }),
  
  saveVote2Comment: (id, comment) =>
    api.post(`/club/submissions/${id}/vote2-comment`, { comment }),
  
  updateStatusFromDropdown: (id, status) =>
    api.put(`/club/submissions/${id}/status-dropdown`, { status }),
  
  getAvailableRuleNumbers: () =>
    api.get('/club/rule-numbers'),
  
  getMaxGrSub: (grNum) =>
    api.get(`/club/rule-numbers/${grNum}/max-sub`),
};

// gr_status CRUD (Admin)
export const statusesApi = {
  getAll: () =>
    api.get('/club/statuses'),
  create: (data) =>
    api.post('/club/statuses', data),
  update: (id, data) =>
    api.put(`/club/statuses/${id}`, data),
  delete: (id) =>
    api.delete(`/club/statuses/${id}`),
};

// Auth API
export const authApi = {
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  register: (data) => 
    api.post('/auth/register', data),
  
  logout: () => 
    api.post('/auth/logout'),
  
  getMe: () => 
    api.get('/auth/me'),

  getProfilePrefs: () =>
    api.get('/profile/prefs'),

  updateProfilePrefs: (data) =>
    api.put('/profile/prefs', data),
  
  forgotPassword: (email) => 
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (data) => 
    api.post('/auth/reset-password', data),
  
  verifyEmail: (email, code) => 
    api.post('/auth/verify-email', { email, code }),
  
  resendVerification: (email) => 
    api.post('/auth/resend-verification', { email }),
};

// Users API
export const usersApi = {
  getAll: (showDeleted = false) => 
    api.get(`/users${showDeleted ? '?deleted=1' : ''}`),
  
  getById: (id) => 
    api.get(`/users/${id}`),
  
  create: (data) => 
    api.post('/users', data),
  
  update: (id, data) => 
    api.put(`/users/${id}`, data),
  
  delete: (id) => 
    api.delete(`/users/${id}`),
  
  restore: (id) => 
    api.post(`/users/${id}/restore`),
};

// Quiz API
export const quizApi = {
  getQuestions: () => 
    api.get('/quiz/questions'),
  
  submitResults: (data) => 
    api.post('/quiz/submit', data),
};

// Quiz Questions Admin API
export const quizQuestionsApi = {
  getAll: () => 
    api.get('/quiz/questions/admin'),
  
  getById: (id) => 
    api.get(`/quiz/questions/admin/${id}`),
  
  create: (data) => 
    api.post('/quiz/questions/admin', data),
  
  update: (id, data) => 
    api.put(`/quiz/questions/admin/${id}`, data),
  
  delete: (id) => 
    api.delete(`/quiz/questions/admin/${id}`),
};

// Glossary API (Public)
export const glossaryApi = {
  // Public endpoints
  getAll: () =>
    api.get('/glossary'),

  getByRuleId: (ruleId) =>
    api.get(`/glossary/rule/${ruleId}`),

  // Admin endpoints
  adminGetAll: () =>
    api.get('/glossary/admin'),

  adminGetById: (id) =>
    api.get(`/glossary/admin/${id}`),

  adminCreate: (data) =>
    api.post('/glossary/admin', data),

  adminUpdate: (id, data) =>
    api.put(`/glossary/admin/${id}`, data),

  adminDelete: (id) =>
    api.delete(`/glossary/admin/${id}`),
};

// Admin Stats
// Stats API
export const statsApi = {
  getSubmissionStats: () =>
    api.get('/stats/submissions'),
};

// Analytics API
export const analyticsApi = {
  track: (data) =>
    api.post('/analytics/track', data),
  
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/analytics/stats${queryString ? '?' + queryString : ''}`);
  },
};

export default api;
